import { useGetAdvantaPlacesByTilesQuery } from '../../../../services/mVPGraphQlService';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { useAreaUnit } from '../../../../utils/functions/countryUtils';
import dayjs from 'dayjs';
import CircularProgress from '@mui/material/CircularProgress';
import * as yup from 'yup';
import { useFormWithSchema } from '../../../../utils/functions/yupFormTypescript';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import MapIcon from '@mui/icons-material/Map';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { MVPFiltersProps } from '../../constants';
import { useFilterContext } from '../../../../services/mVPMapService/filterContext';
import {
  GoogleMap,
  LoadScript,
  Marker,
  Polygon,
  MarkerClusterer,
  Autocomplete,
  OverlayView,
} from '@react-google-maps/api';
import { latLngConversion } from '../../../../utils/functions/maps';
import throttle from 'lodash/throttle';
import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { AutoCompleteController } from '../../../../components';
import MultiSelect from '../../../../components/formControllers/multiSelect';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import MapView from '../../../../assets/images/MapView.png';
import SatelliteView from '../../../../assets/images/SatelliteView.png';
import Arrow from '../../../../assets/images/stash_arrow-up-duotone.png';
import { TAutoCompleteOptions } from '../../../../utils/types';
import SearchIcon from '@mui/icons-material/Search';

const containerStyle = { width: '100%', height: '100vh' };

const tileXYToLatLng = (
  tileX: number,
  tileY: number,
  zoom: number
) => {
  const n = Math.pow(2, zoom);
  const lng = (tileX / n) * 360 - 180;
  const latRad = Math.atan(
    Math.sinh(Math.PI * (1 - (2 * tileY) / n))
  );
  const lat = (latRad * 180) / Math.PI;
  return { lat, lng };
};

const initialTile = {
  zoom: 12,
  tileX: 2907,
  tileY: 1881,
};

const initialZoom = 12;

const initialCenter = tileXYToLatLng(
  initialTile.tileX,
  initialTile.tileY,
  initialTile.zoom
);

export const GoogleMaps: React.FC<MVPFiltersProps> = ({
  onNavigate,
  // tableDataType,
  debouncedIntValue,
}) => {
  const wsId = useSelector(
    (state: RootState) => state.country.wsId
  );
  const areaUnit = useAreaUnit(false);
  const inventoryImportFormSchema = yup.object({
    averageIndexValue: yup
      .string()
      .nullable()
      .default('averageIndexValue'),
    type: yup.string().nullable().default(''),
    subType: yup.string().default(''),
    group: yup
      .array(yup.string())
      .nullable()
      .max(5, 'You can select a maximum of 5'),
  });

  const formReturn = useFormWithSchema(
    inventoryImportFormSchema,
    {
      mode: 'onTouched',
    }
  );

  const { control, watch, setValue } = formReturn;
  const { filterValues: commonFilters, setFilterValues } =
    useFilterContext();
  const {
    regionManagerList,
    tpmList,
    faList,
    hybridTypeList,
    weeksOfSowing,
  } = useFilterContext();
  const [zoom, setZoom] = useState(initialZoom);
  const [mapsApi, setMapsApi] =
    useState<google.maps.Map | null>(null);
  const [dynamicTiles, setDynamicTiles] = useState<
    { zoom: number; tileX: number; tileY: number }[]
  >([]);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [searchBox, setSearchBox] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [closedInfoWindowIds, setClosedInfoWindowIds] =
    useState<Set<string>>(new Set());
  const [showSubTypePanel, setShowSubTypePanel] =
    useState(false);
  const [mapType, setMapType] = useState<
    'satellite' | 'roadmap'
  >('satellite');
  const [showLayers, setShowLayers] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);

  const tileCache = useRef<Set<string>>(
    new Set([
      `${initialTile.zoom}_${initialTile.tileX}_${initialTile.tileY}`,
    ])
  );
  const [error, setError] = useState<string | null>(null);
  const [filterVisible, setFilterVisible] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  const lastTilesRef = useRef<
    { zoom: number; tileX: number; tileY: number }[]
  >([]);

  const latLngToTileXY = useCallback(
    (lat: number, lng: number, zoom: number) => {
      const tileX = Math.floor(
        ((lng + 180) / 360) * Math.pow(2, zoom)
      );
      const tileY = Math.floor(
        ((1 -
          Math.log(
            Math.tan((lat * Math.PI) / 180) +
              1 / Math.cos((lat * Math.PI) / 180)
          ) /
            Math.PI) /
          2) *
          Math.pow(2, zoom)
      );
      return { tileX, tileY };
    },
    []
  );

  const areTilesEqual = (
    tiles1: {
      zoom: number;
      tileX: number;
      tileY: number;
    }[],
    tiles2: { zoom: number; tileX: number; tileY: number }[]
  ) => {
    if (tiles1.length !== tiles2.length) return false;
    const tileSet1 = new Set(
      tiles1.map((t) => `${t.zoom}_${t.tileX}_${t.tileY}`)
    );
    return tiles2.every((t) =>
      tileSet1.has(`${t.zoom}_${t.tileX}_${t.tileY}`)
    );
  };

  const handleMapIdle = useCallback(
    throttle(() => {
      if (!mapRef.current || !mapsApi || !isMapLoaded)
        return;

      const map = mapRef.current;
      const bounds = map.getBounds();
      const zoomLevel = map.getZoom();
      //  const center = map.getCenter();

      if (!bounds || zoomLevel == null) return;

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      const neTile = latLngToTileXY(
        ne.lat(),
        ne.lng(),
        zoomLevel
      );
      const swTile = latLngToTileXY(
        sw.lat(),
        sw.lng(),
        zoomLevel
      );

      const minTileX = Math.min(neTile.tileX, swTile.tileX);
      const maxTileX = Math.max(neTile.tileX, swTile.tileX);
      const minTileY = Math.min(neTile.tileY, swTile.tileY);
      const maxTileY = Math.max(neTile.tileY, swTile.tileY);

      const newTiles: {
        zoom: number;
        tileX: number;
        tileY: number;
      }[] = [];
      for (let x = minTileX; x <= maxTileX; x++) {
        for (let y = minTileY; y <= maxTileY; y++) {
          newTiles.push({
            zoom: zoomLevel,
            tileX: x,
            tileY: y,
          });
        }
      }

      if (
        newTiles.length > 0 &&
        !areTilesEqual(newTiles, lastTilesRef.current)
      ) {
        setDynamicTiles(newTiles);
        lastTilesRef.current = newTiles;
      }
    }, 2000),
    [mapsApi, latLngToTileXY, isMapLoaded]
  );

  // const handleNavigation = (
  //   fragment: string,
  //   id: number
  // ) => {
  //   localStorage.setItem('fieldId', id?.toString());
  //   localStorage.setItem(
  //     'filterIndex',
  //     JSON?.stringify(commonFilters)
  //   );
  //   window.open(
  //     `/mvp/charts#${fragment}`,
  //     '_blank',
  //     'noopener,noreferrer'
  //   );
  // };

  const handleNavigation = (id: number) => {
    localStorage.setItem('fieldId', id?.toString());
    localStorage.setItem(
      'filterIndex',
      JSON?.stringify(commonFilters)
    );
    onNavigate?.('timeseries', {
      fieldId: id?.toString(),
    });
  };

  const getApiFilters = useCallback(() => {
    const type = watch('type');
    const subType = watch('subType');
    const group = watch('group') || [];

    const updatedFilters = { ...commonFilters };

    if (type === 'people' && subType && group.length > 0) {
      if (subType === 'RM') {
        updatedFilters.regionManager = group;
      } else if (subType === 'TPM') {
        updatedFilters.tpm = group;
      } else if (subType === 'FA') {
        updatedFilters.fa = group;
      }
    } else if (type === 'variety') {
      updatedFilters.hybridType = group;
    } else if (type === 'weekofsowing') {
      updatedFilters.weeksOfSowing = group;
    }

    return updatedFilters;
  }, [commonFilters, watch]);

  const [apiFilters, setApiFilters] = useState(
    getApiFilters()
  );

  useEffect(() => {
    setApiFilters(getApiFilters());
  }, [commonFilters, getApiFilters]);

  useEffect(() => {
    setValue('group', []);
  }, [watch('type'), watch('subType'), setValue]);

  const hasRequiredFilters = Boolean(
    apiFilters?.index?.trim() &&
      apiFilters?.season?.trim() &&
      apiFilters?.country?.trim()
  );

  const {
    data: mapsData,
    isFetching: isLoadingMapsData,
    error: apiError,
  } = useGetAdvantaPlacesByTilesQuery(
    dynamicTiles.length && isMapLoaded && hasRequiredFilters
      ? {
          tileRequests: {
            env: {
              sourceType: 'ADVANTA_USER_FIELD',
              namespace: 'FIELDS',
              visibleType: 'VILLAGE_FIELD',
              client: 'DESKTOP_WEB_UI_CLIENT',
            },
            mapTiles: dynamicTiles,
            tagFilterV2: {},
          },
          commonFilters: apiFilters,
          fieldNumberOnly: zoom > 10 ? false : true,
          dop: debouncedIntValue,
          wsId,
        }
      : skipToken
  );

  useEffect(() => {
    if (dynamicTiles.length > 0 && isMapLoaded) {
      console.log('API Call triggered with tiles:', {
        tileCount: dynamicTiles.length,
        tiles: dynamicTiles,
      });
    }
  }, [dynamicTiles, isMapLoaded]);

  const selectedType = watch('type');
  const selectedGroup = watch('group') || [];

  useEffect(() => {
    // Clear subType and group when type changes
    setValue('subType', '');
    setValue('group', []);
    const resetFilters = {
      ...commonFilters,
      regionManager: Array.isArray(
        commonFilters.regionManager
      )
        ? 0
        : commonFilters.regionManager,
      tpm: Array.isArray(commonFilters.tpm)
        ? 0
        : commonFilters.tpm,
      fa: Array.isArray(commonFilters.fa)
        ? 0
        : commonFilters.fa,
      hybridType: Array.isArray(commonFilters.hybridType)
        ? ''
        : commonFilters.hybridType,
      weeksOfSowing: [],
    };
    setApiFilters(resetFilters);
    setFilterValues(resetFilters);
  }, [selectedType, setValue]);

  useEffect(() => {
    if (selectedType === 'people') {
      setShowSubTypePanel(true);
    } else {
      setShowSubTypePanel(false);
    }
  }, [selectedType]);
  const handleApplyFilters = () => {
    const newFilters = getApiFilters();
    setApiFilters(newFilters);
    setFilterValues(newFilters);
    setFilterVisible(false);
    setShowLegend(true);
    tileCache.current.clear();
    handleMapIdle();
  };

  useEffect(() => {
    if (apiError) {
      setError(
        'Failed to fetch map data. Please try again.'
      );
    }
  }, [apiError]);

  const polygons = useMemo(() => {
    const geometryHex =
      mapsData?.data?.getAdvantaPlacesByTiles
        ?.fieldTileResponse?.[0]?.placeField;

    if (!geometryHex) return [];

    return geometryHex.map((item) => ({
      geometry: latLngConversion(item?.place?.geometry),
      fieldDataWf: item?.fieldDataWf,
      place: item?.place,
    }));
  }, [mapsData]);

  const polygonGeometries = useMemo(
    () => polygons.map((poly) => poly.geometry.geometry),
    [polygons]
  );

  const centers = useMemo(
    () =>
      polygonGeometries.map((geometry) => {
        let latSum = 0;
        let lngSum = 0;
        const numPoints = geometry.length;
        geometry.forEach((point) => {
          latSum += point.lat;
          lngSum += point.lng;
        });
        return {
          lat: latSum / numPoints,
          lng: lngSum / numPoints,
        };
      }),
    [polygonGeometries]
  );
  const faMap = Object.fromEntries(
    faList.map((fa) => [fa.label, fa.value])
  );
  const rmMap = Object.fromEntries(
    regionManagerList.map((rm) => [rm.label, rm.value])
  );
  const tpmMap = Object.fromEntries(
    tpmList.map((tpm) => [tpm.label, tpm.value])
  );
  const fieldLocations = useMemo(
    () =>
      polygons.map((polygon, index) => {
        const ndvi =
          polygon?.fieldDataWf?.derivedFieldProps
            ?.overallAvgIndex;
        const sowingDate =
          polygon?.fieldDataWf?.fieldDetails?.sowingTime
            ?.seconds;
        const yieldPerAcre =
          polygon?.fieldDataWf?.derivedFieldProps
            ?.yieldPerAcre;
        const variety =
          polygon?.fieldDataWf?.fieldDetails?.hybridCode;

        return {
          id: polygon?.place?.sourceId,
          lat: centers[index]?.lat || initialCenter.lat,
          lng: centers[index]?.lng || initialCenter.lng,
          date: sowingDate,
          ndvi,
          yieldPerAcre,
          variety,
          polygon: polygon.geometry.geometry,
          people:
            polygon.fieldDataWf.fieldDetails.employeeData,
          hybridCode:
            polygon.fieldDataWf.fieldDetails.hybridCode,
          weeksOfSowing:
            polygon.fieldDataWf.derivedFieldProps
              .weekOfSowing,
        };
      }),
    [polygons, centers]
  );

  const getNDVIColor = () => {
    return '#8CF01A';
  };

  const clusterStyles = [
    {
      url: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI1MCIgaGVpZ2h0PSI1MCI+PHJlY3Qgd2lkdGg9IjUwIiBoZWlnaHQ9IjUwIiByeD0iMjUiIGZpbGw9IiMwMDAwMDAiIC8+PC9zdmc+',
      height: 45,
      width: 45,
      textColor: 'white',
      textSize: 16,
    },
  ];

  const handleMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    if (window.google?.maps) {
      setMapsApi(window.google.maps);
      map.setOptions({
        maxZoom: 18,
        minZoom: 9,
        restriction: {
          latLngBounds: {
            north: 85,
            south: -85,
            east: 180,
            west: -180,
          },
          strictBounds: true,
        },
      });
      setIsMapLoaded(true);

      handleMapIdle();
    }
  };

  const handleZoomChange = () => {
    if (mapRef.current) {
      const currentZoom = mapRef.current.getZoom() || 12;
      if (currentZoom > 18) {
        mapRef.current.setZoom(18);
      }
      setZoom(currentZoom);
      tileCache.current.clear();
      handleMapIdle();
    }
  };

  const changeMapType = (type) => {
    setMapType(type);
    setShowLayers(false);
    if (mapRef.current) {
      mapRef.current.setMapTypeId(type);
    }
  };

  const resetMap = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.panTo(initialCenter);
      mapRef.current.setZoom(initialZoom);
      setZoom(initialZoom);
      tileCache.current.clear();
      handleMapIdle();
      handleClearAll();
      setShowLegend(false);
    }
  }, []);

  const formattedListOfType = [
    {
      value: 'people',
      label: 'People' + `/${watch('subType')}`,
    },
    { value: 'variety', label: 'Variety' },
    { value: 'weekofsowing', label: 'Week of sowing' },
  ];
  const formattedListOfAIV = [
    {
      value: 'averageIndexValue',
      label: 'Average Index Value',
    },
    { value: 'yieldperacre', label: `Yield/${areaUnit}` },
  ];

  useEffect(() => {
    setValue('group', []);
  }, []);

  const handleClearAll = () => {
    setValue('type', '');
    setValue('subType', '');
    setValue('group', []);

    const resetFilters = {
      ...commonFilters,
      regionManager: Array.isArray(
        commonFilters.regionManager
      )
        ? 0
        : commonFilters.regionManager,
      tpm: Array.isArray(commonFilters.tpm)
        ? 0
        : commonFilters.tpm,
      fa: Array.isArray(commonFilters.fa)
        ? 0
        : commonFilters.fa,
      hybridType: Array.isArray(commonFilters.hybridType)
        ? ''
        : commonFilters.hybridType,
      weeksOfSowing: [],
    };
    setApiFilters(resetFilters);
    setFilterValues(resetFilters);
    tileCache.current.clear();
    handleMapIdle();
  };

  const mapOptions = useMemo(
    () => ({
      maxZoom: 18,
      minZoom: 9,
      streetViewControl: false,
      fullscreenControl: false,
      mapTypeControl: false,
      rotateControl: false,
      scaleControl: false,
      zoomControl: true, // ✅ show only zoom in/out
      panControl: false, // 🚫 remove arrow navigation buttons
      draggable: true, // allow mouse dragging

      styles: [
        // Hide all labels by default
        // {
        //   featureType: 'all',
        //   elementType: 'labels',
        //   stylers: [{ visibility: 'off' }],
        // },
        // // Show country labels
        // {
        //   featureType: 'administrative.country',
        //   elementType: 'labels.text',
        //   stylers: [{ visibility: 'on' }],
        // },
        // // Show state labels
        // {
        //   featureType: 'administrative.province',
        //   elementType: 'labels.text',
        //   stylers: [{ visibility: 'on' }],
        // },
        // // Show major city labels
        // {
        //   featureType: 'administrative.locality',
        //   elementType: 'labels.text',
        //   stylers: [{ visibility: 'on' }],
        // },
        // // Optional: keep boundaries visible
        // {
        //   featureType: 'administrative',
        //   elementType: 'geometry.stroke',
        //   stylers: [{ visibility: 'on' }],
        // },
        {
          featureType: 'all',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }],
        },

        {
          featureType: 'administrative.province',
          elementType: 'labels.text',
          stylers: [{ visibility: 'on' }],
        },
      ],
      restriction: {
        latLngBounds: {
          north: 85,
          south: -85,
          east: 180,
          west: -180,
        },
        strictBounds: true,
      },
    }),
    []
  );

  const handleTypeChange = (
    selectedOption: TAutoCompleteOptions | null
  ) => {
    const value = selectedOption?.value as string;
    setValue('type', value ? value : '');
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100vh',
      }}>
      {isLoadingMapsData && (
        <Box
          sx={{
            position: 'absolute',
            top: '76%',
            left: '1%',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            padding: '20px 30px 20px 30px',
            borderRadius: '4px',
            color: '#fff',
            fontSize: '16px',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)', // Safari
            zIndex: 1000,
          }}>
          <CircularProgress
            size={20}
            thickness={4}
            sx={{
              color: '#fff', // white spinner
            }}
          />
          Loading...
        </Box>
      )}
      {error && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            zIndex: 1000,
            bgcolor: 'error.main',
            p: 2,
            borderRadius: 2,
          }}>
          {error}
        </Box>
      )}

      <LoadScript
        googleMapsApiKey={
          process.env.REACT_APP_GOOGLE_MAPS_API_KEY
        }
        libraries={['places']}
        onLoad={() =>
          setMapsApi(window.google?.maps || null)
        }>
        {mapsApi && (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={initialCenter}
            zoom={zoom}
            mapTypeId={mapType}
            onLoad={handleMapLoad}
            onIdle={handleMapIdle}
            onZoomChanged={handleZoomChange}
            options={mapOptions}>
            <Box
              sx={{
                position: 'absolute',
                left: 15,
                top: 20,
                zIndex: 1001,
                width: 320,
                bgcolor: 'white',
                borderRadius: 2,
                boxShadow: 3,
                display: 'flex',
                flexDirection: 'column',
              }}>
              <Autocomplete
                onLoad={setSearchBox}
                onPlaceChanged={() => {
                  const place = searchBox.getPlace();
                  if (
                    place.geometry &&
                    place.geometry.location
                  ) {
                    mapRef.current.panTo({
                      lat: place.geometry.location.lat(),
                      lng: place.geometry.location.lng(),
                    });
                    setZoom(15);
                    tileCache.current.clear();
                    handleMapIdle();

                    setSearchInput(
                      place.formatted_address ||
                        place.name ||
                        ''
                    );
                  }
                }}>
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                  }}>
                  <SearchIcon
                    style={{
                      position: 'absolute',
                      left: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#888',
                      pointerEvents: 'none',
                      fontSize: 20,
                    }}
                  />

                  <input
                    type="text"
                    placeholder="Search by location"
                    style={{
                      boxSizing: 'border-box',
                      border: '1px solid #ccc',
                      padding: '8px 8px 8px 36px',
                      width: '100%',
                      borderRadius: 4,
                      fontSize: 16,
                    }}
                    value={searchInput}
                    onChange={(e) =>
                      setSearchInput(e.target.value)
                    }
                  />
                </div>
              </Autocomplete>
            </Box>
            <div
              style={{
                position: 'absolute',
                top: 80,
                right: 10,
                zIndex: 1000,
              }}>
              <Box
                sx={{
                  position: 'absolute',
                  right: 0,
                  zIndex: 1001,
                }}>
                <IconButton
                  onClick={() => {
                    setShowLayers(!showLayers);
                  }}
                  sx={{
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: '#fff',
                    padding: '8px 10px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: 'bold',
                    height: 45,
                    width: 45,
                    backdropFilter: 'blur(6px)',
                    WebkitBackdropFilter: 'blur(6px)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      color: '#fff',
                    },
                  }}>
                  <LayersOutlinedIcon />
                </IconButton>
              </Box>
              {showLayers && (
                <div
                  style={{
                    marginTop: 0,
                    background: 'rgba(0,0,0,0.85)',
                    padding: '12px',
                    borderRadius: '12px',
                    width: 180,
                    color: '#fff',
                    marginRight: 50,
                  }}>
                  <div style={{ padding: 10 }}>Layers</div>
                  <div
                    onClick={() =>
                      changeMapType('satellite')
                    }
                    style={{
                      padding: '8px',
                      background:
                        mapType === 'satellite'
                          ? '#333'
                          : 'transparent',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    <img
                      src={SatelliteView}
                      alt="Satellite"
                      style={{
                        width: 40,
                        height: 40,
                        marginRight: 8,
                      }}
                    />
                    Satellite
                  </div>
                  <div
                    onClick={() => changeMapType('roadmap')}
                    style={{
                      padding: '8px',
                      background:
                        mapType === 'roadmap'
                          ? '#333'
                          : 'transparent',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    <img
                      src={MapView}
                      alt="Map"
                      style={{
                        width: 40,
                        height: 40,
                        marginRight: 8,
                      }}
                    />
                    Map
                  </div>
                </div>
              )}
            </div>

            <Box
              sx={{
                position: 'absolute',
                top: 24,
                right: 10,
                zIndex: 1001,
              }}>
              <IconButton
                onClick={() => {
                  //   handleClearAll();
                  setFilterVisible(true);
                }}
                sx={{
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: '#fff',
                  borderRadius: 2,
                  p: 1,
                  height: 45,
                  width: 45,
                  backdropFilter: 'blur(6px)',
                  WebkitBackdropFilter: 'blur(6px)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: '#fff',
                  },
                }}>
                <MapIcon />
              </IconButton>
            </Box>
            {filterVisible && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 25,
                  right: 24,
                  zIndex: 1002,
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                  boxShadow: 3,
                  p: 2,
                  width: 310,
                  //   height: 520,
                  marginRight: 5,
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                  }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    <MapIcon
                      sx={{ mr: 1, color: 'white' }}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: '#fff',
                        fontWeight: 'bold',
                      }}>
                      Map View Settings
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() => setFilterVisible(false)}
                    sx={{ color: '#fff' }}>
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    bgcolor: '#000',
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                  }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    <Typography
                      sx={{
                        color: '#fff',
                        mr: 2,
                        width: 600,
                        mb: 1,
                      }}>
                      Card View
                    </Typography>
                  </Box>
                  <Divider
                    sx={{
                      borderColor:
                        'rgba(255, 255, 255, 0.25)',
                      mb: 2,
                    }}
                  />
                  <Box
                    sx={{
                      backgroundColor: 'white',
                      height: 45,
                      borderRadius: 2,
                    }}>
                    <AutoCompleteController
                      control={control}
                      name="averageIndexValue"
                      label=""
                      options={formattedListOfAIV}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    bgcolor: '#000',
                    p: 2,
                    mb: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderRadius: 2,
                  }}>
                  <Box sx={{ width: '100%' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}>
                      <Typography
                        sx={{
                          color: '#fff',
                          mr: 2,
                        }}>
                        Group By
                      </Typography>
                      <button
                        onClick={handleClearAll}
                        style={{
                          color: '#fff',
                          cursor: 'pointer',
                          padding: '8px 8px 8px 8px', // makes clickable area bigger
                          background: 'transparent',
                          border: 'none',
                        }}>
                        Clear All
                      </button>
                    </Box>
                    <Divider
                      sx={{
                        borderColor:
                          'rgba(255, 255, 255, 0.25)',
                        mb: 2,
                      }}
                    />
                    <Box
                      sx={{ position: 'relative', mt: 1 }}>
                      <Typography
                        sx={{
                          color: '#fff',
                          mb: 1,
                          fontSize: 15,
                        }}>
                        Type *
                      </Typography>

                      <Box
                        sx={{
                          backgroundColor: 'white',
                          height: 45,
                          borderRadius: 2,
                        }}>
                        <AutoCompleteController
                          control={control}
                          name="type"
                          label=""
                          options={formattedListOfType}
                          onChange={(value) => {
                            if (selectedType === 'people') {
                              setShowSubTypePanel(true);
                            } else {
                              setShowSubTypePanel(false);
                            }

                            handleTypeChange(value);
                          }}
                        />

                        {selectedType === 'people' &&
                          showSubTypePanel && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                right: '100%',
                                bgcolor: 'white',
                                boxShadow: 1,
                                p: 1,
                                minWidth: 150,
                                zIndex: 1003,
                              }}>
                              <Typography
                                onClick={() => {
                                  setValue('subType', 'RM');
                                  setShowSubTypePanel(
                                    false
                                  );
                                }}
                                sx={{
                                  p: 1,
                                  '&:hover': {
                                    bgcolor: '#f5f5f5',
                                  },
                                  cursor: 'pointer',
                                  fontWeight: 'normal',
                                }}>
                                RM
                              </Typography>
                              <Typography
                                onClick={() => {
                                  setValue(
                                    'subType',
                                    'TPM'
                                  );
                                  setShowSubTypePanel(
                                    false
                                  );
                                }}
                                sx={{
                                  p: 1,
                                  '&:hover': {
                                    bgcolor: '#f5f5f5',
                                  },
                                  cursor: 'pointer',
                                  fontWeight: 'normal',
                                }}>
                                TPM
                              </Typography>
                              <Typography
                                onClick={() => {
                                  setValue('subType', 'FA');
                                  setShowSubTypePanel(
                                    false
                                  );
                                }}
                                sx={{
                                  p: 1,
                                  '&:hover': {
                                    bgcolor: '#f5f5f5',
                                  },
                                  cursor: 'pointer',
                                  fontWeight: 'normal',
                                }}>
                                FA
                              </Typography>
                            </Box>
                          )}
                      </Box>
                    </Box>

                    <Box sx={{ mt: 1 }}>
                      <Typography
                        sx={{
                          color: '#fff',
                          mb: 1,
                          fontSize: 15,
                        }}>
                        {watch('type') === 'people'
                          ? 'People (Max count 5) *'
                          : watch('type') === 'variety'
                          ? 'Variety (Max count 5) *'
                          : watch('type') === 'weekofsowing'
                          ? 'Week of sowing (Max count 5) *'
                          : 'Group *'}
                      </Typography>
                      <Box
                        sx={{
                          backgroundColor: 'white',
                          height: 45,
                          borderRadius: 2,
                        }}>
                        <MultiSelect
                          control={control}
                          name="group"
                          label=""
                          options={
                            (watch('type') === 'people' &&
                              watch('subType')) === 'RM'
                              ? regionManagerList
                              : watch('type') ===
                                  'people' &&
                                watch('subType') === 'TPM'
                              ? tpmList
                              : watch('type') ===
                                  'people' &&
                                watch('subType') === 'FA'
                              ? faList
                              : watch('type') === 'variety'
                              ? hybridTypeList
                              : watch('type') ===
                                'weekofsowing'
                              ? weeksOfSowing
                              : []
                          }
                          showSelectAll={false}
                          maxCount={true}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleApplyFilters}
                  sx={{
                    mt: 1,
                    width: '100%',
                    bgcolor: '#006970',
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#006970' },
                    '&.Mui-disabled': {
                      bgcolor: '#006970',
                      opacity: 0.5,
                      color: '#fff',
                    },
                  }}
                  disabled={
                    selectedType &&
                    selectedGroup.length === 0
                  }>
                  Apply
                </Button>
              </Box>
            )}
            <Box
              sx={{
                position: 'absolute',
                bottom: 132,
                right: 10,
                zIndex: 1001,
              }}>
              <IconButton
                color="default"
                size="large"
                sx={{
                  bgcolor: 'white',
                  borderRadius: 2,
                  boxShadow: 2,
                  height: 45,
                  width: 45,
                  ':hover': {
                    bgcolor: 'white',
                  },
                }}
                onClick={resetMap}>
                <RestoreIcon />
              </IconButton>
            </Box>

            {watch('group')?.length > 0 && showLegend && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 23,
                  right: 80, // right bottom
                  zIndex: 1001,
                  display: 'flex',
                  flexDirection: 'column', // stack title and colors vertically
                  alignItems: 'flex-start',
                  gap: 1.5,
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                  padding: 2,
                  borderRadius: 2,
                  height: 80,
                  backdropFilter: 'blur(6px)',
                  WebkitBackdropFilter: 'blur(6px)',
                }}>
                {/* Title */}
                <Typography
                  sx={{
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 'bold',
                    fontStyle: 'italic',
                  }}>
                  {watch('type')}
                </Typography>

                {/* Color Legend Row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}>
                  {watch('group').map(
                    (groupValue, index) => {
                      const colors = [
                        '#00DDFF',
                        '#00FFAA',
                        '#FFA600',
                        '#D61DFF',
                        '#FFE100',
                      ];

                      // If FA, map ID to name
                      const labelToShow =
                        watch('type') === 'people' &&
                        watch('subType') === 'FA'
                          ? faList.find(
                              (fa) =>
                                fa.value === groupValue
                            )?.label || groupValue
                          : watch('type') === 'people' &&
                            watch('subType') === 'RM'
                          ? regionManagerList.find(
                              (rm) =>
                                rm.value === groupValue
                            )?.label || groupValue
                          : watch('type') === 'people' &&
                            watch('subType') === 'TPM'
                          ? tpmList.find(
                              (tpm) =>
                                tpm.value === groupValue
                            )?.label || groupValue
                          : groupValue;

                      return (
                        <Box
                          key={groupValue}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                          }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 5,
                              backgroundColor:
                                colors[
                                  index % colors.length
                                ],
                              mb: 0.5,
                              borderRadius: 10,
                            }}
                          />
                          <Typography
                            sx={{
                              color: '#fff',
                              fontSize: 12,
                              textAlign: 'center',
                            }}>
                            {labelToShow}
                          </Typography>
                        </Box>
                      );
                    }
                  )}
                </Box>
              </Box>
            )}

            {mapsApi &&
              zoom < 9 &&
              fieldLocations.length > 0 && (
                <Marker
                  position={centers[0] || initialCenter}
                  icon={{
                    path: mapsApi.SymbolPath.CIRCLE,
                    scale: 10,
                    fillColor: '#0000FF',
                    fillOpacity: 0.7,
                    strokeWeight: 1,
                    strokeColor: '#FFFFFF',
                  }}
                  label={{
                    text: fieldLocations.length.toString(),
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                />
              )}
            {mapsApi && zoom >= 9 && zoom <= 12 && (
              <MarkerClusterer
                averageCenter
                enableRetinaIcons
                gridSize={60}
                minimumClusterSize={1}
                styles={clusterStyles}>
                {(clusterer) =>
                  fieldLocations.map((field) => (
                    <Marker
                      key={field.id}
                      position={{
                        lat: field.lat,
                        lng: field.lng,
                      }}
                      clusterer={clusterer}
                      icon={{
                        path: mapsApi.SymbolPath.CIRCLE,
                        scale: 20,
                        fillColor: getNDVIColor(),
                        fillOpacity: 1,
                        strokeColor: '#4C8E00',
                        strokeWeight: 3,
                        strokeOpacity: 1,
                      }}
                    />
                  ))
                }
              </MarkerClusterer>
            )}

            {mapsApi &&
              zoom >= 13 &&
              zoom <= 14 &&
              fieldLocations.map((field) => {
                const groupValues = watch('group') || [];
                const colors = [
                  '#00DDFF',
                  '#00FFAA',
                  '#FFA600',
                  '#D61DFF',
                  '#FFE625',
                ];
                const strokeColors = [
                  '#40A4B4', // contrast for '#00BCD4'
                  '#27B183', // contrast for '#4CAF50'
                  '#BC7A00', // contrast for '#FF9800'
                  '#9312AF', // contrast for '#9C27B0'
                  '#CCB612', // contrast for '#FFEB3B'
                ];
                let color = '#8CF01A';
                let strokeColor = '#4C8E00';
                const matchField =
                  watch('type') === 'people' &&
                  watch('subType') === 'TPM'
                    ? tpmMap[field.people.tpm] || ''
                    : watch('type') === 'people' &&
                      watch('subType') === 'RM'
                    ? rmMap[field.people.regionManager] ||
                      ''
                    : watch('type') === 'people' &&
                      watch('subType') === 'FA'
                    ? faMap[field.people.fa] || ''
                    : watch('type') === 'variety'
                    ? field.hybridCode || ''
                    : watch('type') === 'weekofsowing'
                    ? field.weeksOfSowing
                    : '';

                const groupIndex =
                  groupValues.indexOf(matchField);
                if (
                  groupIndex !== -1 &&
                  groupIndex < colors.length
                ) {
                  color = colors[groupIndex];
                  strokeColor = strokeColors[groupIndex];
                }

                return (
                  <React.Fragment key={field.id}>
                    <MarkerClusterer
                      averageCenter
                      enableRetinaIcons
                      gridSize={60}
                      minimumClusterSize={2}
                      styles={[
                        {
                          url: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m1.png',
                          height: 50,
                          width: 50,
                          textColor: 'white',
                          textSize: 12,
                        },
                      ]}>
                      {(clusterer) => (
                        <Marker
                          key={field.id}
                          position={{
                            lat: field.lat,
                            lng: field.lng,
                          }}
                          clusterer={clusterer}
                          icon={{
                            path: mapsApi.SymbolPath.CIRCLE,
                            scale: 10,
                            fillColor: color,
                            fillOpacity: 1,
                            strokeColor: strokeColor,
                            strokeWeight: 2,
                            strokeOpacity: 1,
                          }}
                        />
                      )}
                    </MarkerClusterer>
                  </React.Fragment>
                );
              })}
            {mapsApi &&
              zoom >= 15 &&
              fieldLocations.map((field) => {
                const groupValues = watch('group') || [];
                const colors = [
                  '#00DDFF',
                  '#00FFAA',
                  '#FFA600',
                  '#D61DFF',
                  '#FFE625',
                ];
                const strokeColors = [
                  '#40A4B4', // contrast for '#00BCD4'
                  '#27B183', // contrast for '#4CAF50'
                  '#BC7A00', // contrast for '#FF9800'
                  '#9312AF', // contrast for '#9C27B0'
                  '#CCB612', // contrast for '#FFEB3B'
                ];
                let color = '#8CF01A';
                let strokeColor = '#4C8E00';

                const matchField =
                  watch('type') === 'people' &&
                  watch('subType') === 'TPM'
                    ? tpmMap[field.people.tpm] || ''
                    : watch('type') === 'people' &&
                      watch('subType') === 'RM'
                    ? rmMap[field.people.regionManager] ||
                      ''
                    : watch('type') === 'people' &&
                      watch('subType') === 'FA'
                    ? faMap[field.people.fa] || ''
                    : watch('type') === 'variety'
                    ? field.hybridCode || ''
                    : watch('type') === 'weekofsowing'
                    ? field.weeksOfSowing
                    : '';

                const groupIndex =
                  groupValues.indexOf(matchField);
                if (
                  groupIndex !== -1 &&
                  groupIndex < colors.length
                ) {
                  color = colors[groupIndex];
                  strokeColor = strokeColors[groupIndex];
                }
                const isClosed = closedInfoWindowIds.has(
                  field.id
                );
                return (
                  <Marker
                    key={field.id}
                    position={{
                      lat: field.lat,
                      lng: field.lng,
                    }}
                    icon={{
                      path: mapsApi.SymbolPath.CIRCLE,
                      scale: 8,
                      fillColor: color,
                      fillOpacity: 1,
                      strokeColor: strokeColor,
                      strokeWeight: 2,
                      strokeOpacity: 1,
                    }}
                    onClick={() => {
                      setClosedInfoWindowIds((prev) => {
                        const newSet = new Set(prev);
                        newSet.delete(field.id); //  reopen the card
                        return newSet;
                      });
                    }}>
                    {!isClosed && (
                      <OverlayView
                        position={{
                          lat: field.lat,
                          lng: field.lng,
                        }}
                        mapPaneName={
                          OverlayView.OVERLAY_MOUSE_TARGET
                        }>
                        <div
                          onMouseEnter={() =>
                            setHoveredId(field.id)
                          }
                          onMouseLeave={() =>
                            setHoveredId(null)
                          }
                          style={{
                            backgroundColor: '#fff',
                            // paddingTop: '10px',
                            borderRadius: '10px',
                            boxShadow:
                              '0 4px 6px rgba(0, 0, 0, 0.1)',
                            fontFamily: 'Arial, sans-serif',
                            color: '#333',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '6px',
                            width: 150,
                            height: 100,
                            position: 'relative',
                            zIndex:
                              hoveredId === field.id
                                ? 9999
                                : 1,
                            transform:
                              hoveredId === field.id
                                ? 'scale(1.05)'
                                : 'scale(1)',
                            transition:
                              'transform 0.2s ease, z-index 0.2s ease',
                          }}>
                          {/* Header row with ID and navigation button */}
                          <div
                            style={{
                              backgroundColor: '#f0f0f0',
                              padding: '8px',
                            }}>
                            <div
                              style={{
                                fontSize: '12px',
                                fontWeight: 'bold',
                                // borderBottom:
                                //   '1px solid #ddd',
                                paddingBottom: '2px',
                                display: 'flex',
                                justifyContent:
                                  'space-between',
                                alignItems: 'center',
                              }}>
                              <span>{field.id}</span>
                              <IconButton
                                onClick={() =>
                                  handleNavigation(field.id)
                                }
                                sx={{
                                  padding: 0,
                                  color: '#333',
                                  fontSize: '16px',
                                }}>
                                <img
                                  src={Arrow}
                                  alt="Navigate"
                                  style={{
                                    width: 20,
                                    height: 20,
                                    marginRight: 8,
                                  }}
                                />
                              </IconButton>
                            </div>
                            <div
                              style={{
                                fontSize: '11px',
                                fontStyle: 'italic',
                              }}>
                              {field?.date &&
                              field?.date > 0
                                ? dayjs(
                                    field.date * 1000
                                  ).format('DD/MM/YYYY')
                                : '-'}{' '}
                              <span
                                style={{
                                  color: '#888',
                                  fontSize: '16px',
                                  verticalAlign: 'middle',
                                }}>
                                •
                              </span>{' '}
                              {field?.variety
                                ? field?.variety
                                : '-'}
                            </div>
                          </div>

                          {watch('averageIndexValue') ===
                          'averageIndexValue' ? (
                            <div
                              style={{
                                fontSize: '12px',
                                color: 'rgba(0, 0, 0, 0.5)',
                                paddingLeft: '8px',
                                paddingRight: '8px',
                              }}>
                              Avg Index (
                              {commonFilters?.index}):{' '}
                              <span
                                style={{
                                  fontWeight: 'bold',
                                  marginTop: 2,
                                  color: '#000000',
                                }}>
                                {field.ndvi}
                              </span>
                            </div>
                          ) : (
                            <div
                              style={{
                                fontSize: '12px',
                                color: 'rgba(0, 0, 0, 0.5)',
                                paddingLeft: '8px',
                                paddingRight: '8px',
                              }}>
                              Yield per {areaUnit}:{' '}
                              <span
                                style={{
                                  fontWeight: 'bold',
                                  marginTop: 2,
                                  color: '#000000',
                                }}>
                                {field.yieldPerAcre > 0
                                  ? field.yieldPerAcre
                                  : '--'}
                              </span>
                            </div>
                          )}
                          <IconButton
                            onClick={() =>
                              setClosedInfoWindowIds(
                                (prev) =>
                                  new Set(prev).add(
                                    field.id
                                  )
                              )
                            }
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              background: '#fff',
                              boxShadow:
                                '0 2px 4px rgba(0,0,0,0.2)',
                              padding: '2px',
                              '&:hover': {
                                background: '#f5f5f5',
                              },
                            }}
                            size="small">
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </OverlayView>
                    )}
                  </Marker>
                );
              })}
            {mapsApi &&
              zoom >= 17 &&
              fieldLocations.map((field) => {
                const groupValues = watch('group') || [];
                const colors = [
                  '#00DDFF',
                  '#00FFAA',
                  '#FFA600',
                  '#D61DFF',
                  '#FFE625',
                ];
                const strokeColors = [
                  '#40A4B4', // contrast for '#00BCD4'
                  '#27B183', // contrast for '#4CAF50'
                  '#BC7A00', // contrast for '#FF9800'
                  '#9312AF', // contrast for '#9C27B0'
                  '#CCB612', // contrast for '#FFEB3B'
                ];
                let color = '#8CF01A';
                let strokeColor = '#4C8E00';

                const matchField =
                  watch('type') === 'people' &&
                  watch('subType') === 'TPM'
                    ? tpmMap[field.people.tpm] || ''
                    : watch('type') === 'people' &&
                      watch('subType') === 'RM'
                    ? rmMap[field.people.regionManager] ||
                      ''
                    : watch('type') === 'people' &&
                      watch('subType') === 'FA'
                    ? faMap[field.people.fa] || ''
                    : watch('type') === 'variety'
                    ? field.hybridCode || ''
                    : watch('type') === 'weekofsowing'
                    ? field.weeksOfSowing
                    : '';

                const groupIndex =
                  groupValues.indexOf(matchField);
                if (
                  groupIndex !== -1 &&
                  groupIndex < colors.length
                ) {
                  color = colors[groupIndex];
                  strokeColor = strokeColors[groupIndex];
                }

                return (
                  <Polygon
                    key={field.id}
                    path={field.polygon}
                    options={{
                      fillColor: color,
                      fillOpacity: 0.6,
                      strokeColor: strokeColor,
                      strokeWeight: 2,
                      strokeOpacity: 1,
                    }}
                  />
                );
              })}
          </GoogleMap>
        )}
      </LoadScript>
    </Box>
  );
};
